/**
 * EquipmentOverviewCard (Phase 4) — a composite dashboard widget that
 * presents ONE equipment as a unit: its sensor ports + parameters shown
 * as live tiles (value + unit + live dot), resolved from
 * /v1/twin/entities/{id}/port-values in live mode (polled 5s), or the
 * entity's static template parameters in stub mode.
 *
 * Entity selection (no dashboard-editor surgery): config.entityId wins,
 * else the dataSource alias matched to an entity's instance name, else the
 * first equipment on the plant. Per-widget entity picking via the config
 * panel is a follow-up; this renders a useful live card out of the box.
 */

import { useEffect, useMemo, useState } from "react";
import { Boxes, Wifi, Zap } from "lucide-react";

import { usePlant } from "@/lib/plant/plant-store";
import { twinBindings, isLiveApi, type PortValue } from "@/lib/api";
import {
  ICON_MAP,
  energyColor,
  materialColor,
  portKind,
  type EntityDef,
  type PlantNode,
  type PortDef,
} from "@foundry/foundry-plant";
import { ShapeValue, isNonScalarKind } from "@foundry/ShapeValue";
import type { DataSource } from "@/lib/dashboard/types";
import { Badge } from "@/lib/ui/badge";
import { cn } from "@/lib/ui/cn";
import { useDisplayUnits } from "@/lib/api/display-units";
import { ChartSkeleton } from "./ChartSkeleton";
import { liveTileOf } from "./live-tile";

interface Slot {
  key: string;
  label: string;
  unit?: string;
  staticVal?: number | string;
  dataKind?: string;
}

function slotsOf(e: EntityDef): Slot[] {
  const out: Slot[] = [];
  for (const p of [...e.inputPorts, ...e.outputPorts]) {
    if (p.dataKind) out.push({ key: p.id, label: p.name, dataKind: p.dataKind });
  }
  for (const p of e.parameters) {
    if (p.dataKind)
      out.push({ key: p.name, label: p.name, unit: p.unit, staticVal: p.value, dataKind: p.dataKind });
  }
  return out;
}

/** Material + energy ports (no telemetry dataKind) shown as colored chips. */
function PortChips({ entity }: { entity: EntityDef }) {
  const ports = [...entity.inputPorts, ...entity.outputPorts].filter((p) => !p.dataKind);
  if (ports.length === 0) return null;
  const isInput = (p: PortDef) => entity.inputPorts.some((q) => q.id === p.id);
  return (
    <div className="mb-2 flex flex-wrap gap-1">
      {ports.map((p) => {
        const isEnergy = portKind(p) === "energy";
        const color = isEnergy ? energyColor(p.energyClass!) : materialColor(p.materialClass);
        const cls = p.energyClass ?? p.materialClass ?? "";
        return (
          <span
            key={p.id}
            className="flex items-center gap-1 rounded-full border border-border bg-surface-muted px-1.5 py-0.5 text-[9px] text-on-surface"
            title={`${cls} · ${isInput(p) ? "in" : "out"}`}
          >
            {isEnergy ? (
              <Zap className="size-2.5" style={{ color, fill: color }} />
            ) : (
              <span className="size-1.5 rounded-full" style={{ background: color }} />
            )}
            {p.name.replace(/_/g, " ")}
          </span>
        );
      })}
    </div>
  );
}



export function EquipmentOverviewCard({
  title,
  subtitle,
  config,
  dataSource,
}: {
  title?: string;
  subtitle?: string;
  config?: Record<string, unknown>;
  dataSource?: DataSource;
}) {
  const { nodes, loading: plantLoading } = usePlant();
  const { shown: prefs } = useDisplayUnits();

  const node = useMemo<PlantNode | null>(() => {
    const id = typeof config?.entityId === "string" ? config.entityId : "";
    if (id) {
      const byId = nodes.find((n) => n.id === id);
      if (byId) return byId;
    }
    const alias = dataSource?.alias;
    if (alias) {
      const byAlias = nodes.find((n) => n.instanceName === alias);
      if (byAlias) return byAlias;
    }
    // Prefer an EQUIPMENT/INSTRUMENT with bindable slots; else the first node.
    return (
      nodes.find((n) => slotsOf(n.entity).length > 0) ?? nodes[0] ?? null
    );
  }, [nodes, config, dataSource]);

  const [pv, setPv] = useState<Record<string, PortValue>>({});
  // The equipment whose values have been answered for: until then a tile's dash means nothing.
  const [readFor, setReadFor] = useState<string | null>(null);
  useEffect(() => {
    setPv({});
    if (!isLiveApi || !node) return;
    let cancelled = false;
    const load = () =>
      twinBindings
        .portValues(node.id)
        .then((r) => {
          if (cancelled) return;
          const m: Record<string, PortValue> = {};
          r.ports.forEach((p) => {
            m[p.port] = p;
          });
          setPv(m);
          setReadFor(node.id);
        })
        .catch(() => {
          if (!cancelled) setReadFor(node.id);
        });
    load();
    const t = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id]);

  if (!node) {
    if (isLiveApi && plantLoading) return <ChartSkeleton shape="equipment" />;
    return (
      <div className="flex h-full items-center justify-center text-center text-xs text-on-surface-muted">
        No equipment yet — create one in Twin → Equipment.
      </div>
    );
  }

  const slots = slotsOf(node.entity);
  const Icon = ICON_MAP[node.entity.iconKey] ?? Boxes;
  const liveCount = isLiveApi
    ? slots.filter((s) => pv[s.key]?.value != null).length
    : 0;
  const reading = isLiveApi && readFor !== node.id;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="mb-2 flex items-center gap-2">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-muted text-brand">
          <Icon className="size-4" />
        </div>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-on-surface">
            {title || node.instanceName}
          </span>
          <span className="truncate text-[10px] uppercase tracking-wide text-on-surface-muted">
            {subtitle || node.entity.class}
          </span>
        </div>
        {isLiveApi && liveCount > 0 && (
          <span className="ml-auto flex shrink-0 items-center gap-1 text-[10px] text-success">
            <Wifi className="size-3" />
            live
          </span>
        )}
      </div>

      {/* Material + energy ports */}
      <PortChips entity={node.entity} />

      {/* Port/parameter tiles */}
      {slots.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-xs text-on-surface-muted">
          This equipment has no sensor slots.
        </div>
      ) : reading ? (
        <ChartSkeleton shape="tiles" count={slots.length} className="min-h-0 flex-1" />
      ) : (
        <div className="grid flex-1 auto-rows-min grid-cols-2 gap-1.5 overflow-auto">
          {slots.map((s) => {
            const live = pv[s.key];
            const kind = s.dataKind ?? live?.data_kind;
            // array/batch slots render a sparkline / mini table spanning the
            // full tile width rather than a single numeric value.
            if (isNonScalarKind(kind)) {
              const shape = isLiveApi && live ? <ShapeValue dataKind={kind} live={live} /> : null;
              return (
                <div
                  key={s.key}
                  className={cn(
                    "col-span-2 flex flex-col gap-0.5 rounded-md border border-border bg-surface-elevated px-2 py-1.5",
                    shape && "border-success/30",
                  )}
                >
                  <span className="flex items-center justify-between gap-1 truncate text-[10px] text-on-surface-muted">
                    <span className="truncate">{s.label.replace(/_/g, " ")}</span>
                    <span className="shrink-0 rounded bg-surface-muted px-1 text-[8px] font-semibold uppercase tracking-wide">
                      {kind === "event_stream" ? "batch" : "array"}
                    </span>
                  </span>
                  {shape ?? (
                    <span className="font-mono text-[10px] text-on-surface-muted">no live value</span>
                  )}
                </div>
              );
            }
            const hasLive = isLiveApi && live != null && live.value != null;
            // In live mode an unresolved scalar shows an honest dash — never the
            // entity's static template value (that's fabricated "old content").
            // Stub mode (no backend) still shows the template value for the demo.
            // The reading and its unit move into the org's units together (liveTileOf).
            const tile = hasLive ? liveTileOf(live!.value!, live!.unit, s.unit, prefs) : null;
            const value = tile ? tile.value : isLiveApi ? "—" : s.staticVal ?? "—";
            const unit = tile ? tile.unit : s.unit;
            return (
              <div
                key={s.key}
                className={cn(
                  "flex flex-col gap-0.5 rounded-md border border-border bg-surface-elevated px-2 py-1.5",
                  hasLive && "border-success/30",
                )}
              >
                <span className="flex items-center gap-1 truncate text-[10px] text-on-surface-muted">
                  {hasLive && <span className="size-1 shrink-0 rounded-full bg-success" />}
                  {s.label.replace(/_/g, " ")}
                </span>
                <span className="truncate font-mono text-sm font-semibold text-on-surface">
                  {value}
                  {unit ? <span className="ml-0.5 text-[10px] font-normal text-on-surface-muted">{unit}</span> : null}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="mt-1.5 flex items-center justify-between text-[10px] text-on-surface-muted">
        <span>{slots.length} datapoint{slots.length === 1 ? "" : "s"}</span>
        {isLiveApi ? (
          !reading && (
            <Badge variant={liveCount > 0 ? "success" : "secondary"} className="text-[8px]">
              {liveCount > 0 ? `${liveCount} live` : "no bindings"}
            </Badge>
          )
        ) : (
          <Badge variant="secondary" className="text-[8px]">template</Badge>
        )}
      </div>
    </div>
  );
}
